/**
 * Number formatting for a trading terminal.
 *
 * Everything here is allocation-conscious: these run inside render loops at
 * 60fps across several panels, so the fast paths avoid Intl.NumberFormat
 * (which is ~20x slower than manual toFixed for small numbers) and cache
 * formatter instances where Intl genuinely is needed.
 */

const groupCache = new Map<number, Intl.NumberFormat>();

function grouped(decimals: number): Intl.NumberFormat {
  let f = groupCache.get(decimals);
  if (!f) {
    f = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: true,
    });
    groupCache.set(decimals, f);
  }
  return f;
}

/**
 * Infer sensible display precision from a price magnitude. Binance gives us
 * exchange tick sizes via exchangeInfo, but we want something usable before
 * that request lands (and for symbols we never queried).
 */
export function inferPricePrecision(price: number): number {
  const p = Math.abs(price);
  if (p === 0 || !Number.isFinite(p)) return 2;
  if (p >= 10000) return 2;
  if (p >= 100) return 2;
  if (p >= 1) return 4;
  if (p >= 0.01) return 5;
  if (p >= 0.0001) return 6;
  return 8;
}

/** Count decimals actually present in an exchange-provided string, e.g. "0.00010000" -> 4. */
export function decimalsFromTick(tick: string): number {
  const dot = tick.indexOf('.');
  if (dot < 0) return 0;
  // Trailing zeros are not significant for a tick size.
  let end = tick.length - 1;
  while (end > dot && tick.charCodeAt(end) === 48) end--;
  return end - dot;
}

/** Fixed-decimal, no grouping. The hot path for order book / tape cells. */
export function fixed(value: number, decimals: number): string {
  if (!Number.isFinite(value)) return '—';
  return value.toFixed(decimals);
}

/** Fixed-decimal with thousands separators. For axis labels and headline prices. */
export function price(value: number, decimals: number): string {
  if (!Number.isFinite(value)) return '—';
  return grouped(decimals).format(value);
}

/** Signed percentage, always with a sign so columns stay aligned. */
export function pct(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return '—';
  const s = value.toFixed(decimals);
  return value > 0 ? `+${s}%` : `${s}%`;
}

/** Signed absolute change. */
export function signed(value: number, decimals: number): string {
  if (!Number.isFinite(value)) return '—';
  const s = value.toFixed(decimals);
  return value > 0 ? `+${s}` : s;
}

/**
 * Compact volume: 1.24K / 18.6M / 2.03B. Fixed 3 significant-ish digits so the
 * column width never jumps around.
 */
export function compact(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const a = Math.abs(value);
  if (a >= 1e12) return (value / 1e12).toFixed(2) + 'T';
  if (a >= 1e9) return (value / 1e9).toFixed(2) + 'B';
  if (a >= 1e6) return (value / 1e6).toFixed(2) + 'M';
  if (a >= 1e3) return (value / 1e3).toFixed(2) + 'K';
  if (a >= 1) return value.toFixed(2);
  if (a === 0) return '0';
  return value.toFixed(4);
}

/** Quantity display — crypto sizes span many orders of magnitude. */
export function qty(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const a = Math.abs(value);
  if (a >= 1e6) return (value / 1e6).toFixed(2) + 'M';
  if (a >= 1000) return value.toFixed(1);
  if (a >= 1) return value.toFixed(3);
  if (a >= 0.001) return value.toFixed(4);
  return value.toFixed(6);
}

const pad2 = (n: number) => (n < 10 ? '0' + n : '' + n);
const pad3 = (n: number) => (n < 10 ? '00' + n : n < 100 ? '0' + n : '' + n);

/** HH:MM:SS in local time. */
export function hms(ms: number): string {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

/** HH:MM:SS.mmm — the tape wants milliseconds. */
export function hmsMilli(ms: number): string {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}.${pad3(d.getMilliseconds())}`;
}

export function hm(ms: number): string {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function dayMonth(ms: number): string {
  const d = new Date(ms);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
}

/** Human-readable age, for "last message 4s ago". */
export function ago(ms: number): string {
  if (ms < 1000) return `${ms | 0}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000) return `${(ms / 60_000) | 0}m`;
  return `${(ms / 3_600_000) | 0}h`;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * "Nice" step for an axis: 1, 2, 2.5, 5, 10 x 10^n. Used by both the price
 * axis and the depth histogram so their gridlines agree visually.
 */
export function niceStep(rough: number): number {
  if (!(rough > 0) || !Number.isFinite(rough)) return 1;
  const exp = Math.floor(Math.log10(rough));
  const pow = Math.pow(10, exp);
  const frac = rough / pow;
  let nice: number;
  if (frac <= 1) nice = 1;
  else if (frac <= 2) nice = 2;
  else if (frac <= 2.5) nice = 2.5;
  else if (frac <= 5) nice = 5;
  else nice = 10;
  return nice * pow;
}
